// @ts-nocheck
import { useState } from 'react'
import { useGetAllUsersQuery, useUpdateUserRoleMutation, useCreateUserMutation } from '../../services/admin.service'
import { Table, Container, Button, Form, Alert, Badge, Row, Col, Card } from 'react-bootstrap'

function UserManagement(): JSX.Element {
  const { data: users, isLoading, error, refetch } = useGetAllUsersQuery({})
  const [updateUserRole, { isLoading: isUpdating }] = useUpdateUserRoleMutation()
  const [createUser, { isLoading: isCreating, error: createError }] = useCreateUserMutation()
  const [selectedRole, setSelectedRole] = useState<{ [key: number]: string }>({})
  
  // New user form state
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'editor'
  })
  const [showForm, setShowForm] = useState(false)
  const [formError, setFormError] = useState('')
  const [formSuccess, setFormSuccess] = useState('')

  const handleRoleChange = (userId: number, role: string) => {
    setSelectedRole({
      ...selectedRole,
      [userId]: role
    })
  }

  const handleUpdateRole = async (userId: number) => {
    try {
      await updateUserRole({ id: userId, role: selectedRole[userId] }).unwrap()
      refetch()
    } catch (error) {
      console.error('Failed to update user role:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setFormSuccess('')

    // Simple validation
    if (!newUser.username || !newUser.email || !newUser.password || !newUser.firstName || !newUser.lastName) {
      setFormError('All fields are required')
      return
    }

    if (newUser.password.length < 6) {
      setFormError('Password must be at least 6 characters')
      return
    }

    try {
      await createUser(newUser).unwrap()
      setFormSuccess('User created successfully')
      setNewUser({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'editor'
      })
      refetch()
    } catch (error: any) {
      setFormError(error?.data?.message || 'Failed to create user')
      console.error('Failed to create user:', error)
    }
  }

  const toggleForm = () => {
    setShowForm(!showForm)
    setFormError('')
    setFormSuccess('')
  }

  if (isLoading) {
    return (
      <Container className="mt-4">
        <Alert variant="info">Loading users...</Alert>
      </Container>
    )
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          Error loading users. Please try again later.
        </Alert>
      </Container>
    )
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h1>User Management</h1>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={toggleForm}>
            {showForm ? 'Hide Form' : 'Add New User'}
          </Button>
        </Col>
      </Row>
      
      {showForm && (
        <Card className="mb-4">
          <Card.Header>Add New User</Card.Header>
          <Card.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            {formSuccess && <Alert variant="success">{formSuccess}</Alert>}
            {createError && <Alert variant="danger">Error: {createError.toString()}</Alert>}
            
            <Form onSubmit={handleCreateUser}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="username" 
                      value={newUser.username} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control 
                      type="email" 
                      name="email" 
                      value={newUser.email} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="firstName" 
                      value={newUser.firstName} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="lastName" 
                      value={newUser.lastName} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control 
                      type="password" 
                      name="password" 
                      value={newUser.password} 
                      onChange={handleInputChange} 
                      required 
                      minLength={6}
                    />
                    <Form.Text className="text-muted">Password must be at least 6 characters</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Role</Form.Label>
                    <Form.Select 
                      name="role" 
                      value={newUser.role} 
                      onChange={handleInputChange} 
                      required
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="editor">Editor</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Button variant="success" type="submit" disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Create User'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}
      <Table striped bordered hover responsive className="mt-4">
        <thead>
          <tr>
            <th>ID</th>
            <th>Username</th>
            <th>Email</th>
            <th>Full Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users?.map((user: any) => (
            <tr key={user.id}>
              <td>{user.id}</td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>{`${user.firstName} ${user.lastName}`}</td>
              <td>
                <Badge bg={user.role === 'admin' ? 'danger' : 'primary'}>
                  {user.role}
                </Badge>
              </td>
              <td>{user.status}</td>
              <td>{new Date(user.lastLogin).toLocaleString()}</td>
              <td>
                <Form.Select
                  size="sm"
                  className="mb-2"
                  value={selectedRole[user.id] || user.role}
                  onChange={(e) => handleRoleChange(user.id, e.target.value)}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="editor">Editor</option>
                </Form.Select>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => handleUpdateRole(user.id)}
                  disabled={!selectedRole[user.id] || selectedRole[user.id] === user.role || isUpdating}
                >
                  Update Role
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  )
}

export default UserManagement
